import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot} from '@angular/router';
import {AuthService} from '../../services/auth/auth.service';
import {RouteService} from '../../services/route/route.service';
import {AuthGuard} from "../auth/auth.guard";
import {firstValueFrom} from "rxjs";
import {ViewService} from '../../services/view/view.service';

@Injectable({
  providedIn: 'root'
})
export class PrivilegeGuard implements CanActivate {
  constructor(private authGuard: AuthGuard,
              private authService: AuthService,
              private router: Router,
              private routeService: RouteService,
              private viewService: ViewService) {
  }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): boolean {
    const hasAccessToken = firstValueFrom(this.authGuard.canActivate(next, state));
    if (!hasAccessToken) {return false;}
    let canAccess = true;
    let redirectToReports = false;
    let redirectToAdmin = false;
    let redirectToBOEvents = false;
    const url = next.url.join('/');
    if (url === 'sales-terminal' && !this.authService.canAccessSales) {
      canAccess = false;
      redirectToReports = true;
    }
    if (url === 'admin' && !this.authService.canAccessAdmin) {
      canAccess = false;
    }
    if (url === 'back-office' && !this.authService.canAccessBackOffice) {
      canAccess = false;
      redirectToReports = true;
    }
    if (url === 'reports' && !this.authService.canAccessReports) {
      canAccess = false;
      redirectToAdmin = true;
    }
    if (this.routeService.checkRoute(['sales-terminal', 'transactions'], state.url) && !this.authService.canAccessSales) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['sales-terminal', 'tickets'], state.url) && !this.authService.hasTicketToolPrivilege) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['sales-terminal', 'invoices'], state.url) && !this.authService.canAccessSales) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['sales-terminal', 'bookings'], state.url) && !this.authService.hasBookingViewPrivilege) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['sales-terminal', 'refund'], state.url) && !this.authService.hasRefundAccessPrivilege) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['sales-terminal', 'payment-reversal'], state.url) && !this.authService.hasSelfServicePaymentReversalPrivilege) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['admin', 'users'], state.url) && !this.authService.hasUserGetPrivilege) {
      redirectToAdmin = true;
      canAccess = false;
    }
    if (this.routeService.checkRoute(['admin', 'users', 'create'], state.url) && !this.authService.hasUserManagePrivilege) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['admin', 'users', 'edit'], state.url) && !this.authService.hasUserManagePrivilege) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['admin', 'legal-persons'], state.url) && !this.authService.hasLegalGetPrivilege) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['admin', 'legal-persons', 'create'], state.url) && !this.authService.hasLegalChangePrivilege) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['admin', 'legal-persons', 'edit'], state.url) && !this.authService.hasLegalChangePrivilege) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['admin', 'customers'], state.url) && !this.authService.hasCustomersAccessPrivileges) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['admin', 'roles'], state.url) && !this.authService.hasRolesAccessPrivilege) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['admin', 'privileges'], state.url) && !this.authService.hasPrivilegesManagePrivilege) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['admin', 'salespoints'], state.url) && !this.authService.hasSalespointManagePrivilege) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['admin', 'registration-data'], state.url) && !this.authService.hasRegistrationDataPrivilege) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['admin', 'api-users'], state.url) && !this.authService.hasApiUsersPrivilege) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['admin', 'commission-fees'], state.url) && !this.authService.hasFeesAccessPrivilege) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['admin', 'service-fees'], state.url) && !this.authService.hasFeesAccessPrivilege) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['admin', 'delivery-types'], state.url) && !this.authService.hasFeesAccessPrivilege) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['admin', 'translations'], state.url) && !this.authService.hasTranslationsPrivilege) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['admin', 'translations', 'download'], state.url) && !this.authService.hasTranslationsDownloadPrivilege) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['admin', 'templates'], state.url) && !this.authService.hasTranslationsPrivilege) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['back-office', 'venues', 'create'], state.url) && !this.authService.hasSelfServiceEventsAccessPrivilege) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['back-office', 'venues', 'edit'], state.url) && !this.authService.hasSelfServiceEventsAccessPrivilege) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['reports', 'daily-reports'], state.url) && !this.authService.hasDailyReportPrivilege) {
      redirectToReports = true;
      canAccess = false;
    }
    if (this.routeService.checkRoute(['reports', 'ticket-registration'], state.url) && !this.authService.hasTicketRegistrationPrivilege) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['reports', 'ticket-registration-reports'], state.url) && !this.authService.hasRegistrationReportPrivilege) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['reports', 'overview'], state.url) && !this.authService.hasReportingPromoterDashboardPrivilege) {
      redirectToReports = true;
      canAccess = false;
    }
    if (this.routeService.checkRoute(['reports', 'events'], state.url) && !this.authService.hasReportingPromoterMyEventsPrivilege) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['reports', 'venue-events'], state.url) && !this.authService.hasReportingVenueOwnerPrivilege) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['reports', 'detailed-reports'], state.url) && !this.authService.hasReportingPromoterDetailedReportsPrivilege) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['reports', 'salespoint-sales'], state.url) && !this.authService.hasReportingSalespointSalesPrivilege) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['reports', 'commission-fees'], state.url) && (!this.authService.hasReportingPromoterMyEventsPrivilege || !this.viewService.conf.environment?.length)) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['reports', 'over-events-report'], state.url) && !this.authService.hasOverEventsReportPrivilege) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['reports', 'sales-summary-report'], state.url) && !this.authService.hasReportingSalesSummaryReportPrivilege) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['reports', 'invoices-report'], state.url) && !this.authService.hasLegalInvoicingViewPrivilege) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['reports', 'balance-summary'], state.url) && !this.authService.hasBalanceSummaryViewPrivilege) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['back-office', 'dashboard'], state.url) && !this.authService.hasAdminDashboardPrivilege) {
      canAccess = false;
      redirectToBOEvents = true;
    }
    if (this.routeService.checkRoute(['back-office', 'events'], state.url) && !this.authService.hasSelfServiceEventsAccessPrivilege) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['back-office', 'events', 'create'], state.url) && !this.authService.hasSelfServiceEventsManagePrivilege) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['back-office', 'events', 'edit'], state.url) && !this.authService.hasSelfServiceEventsManagePrivilege) {
      canAccess = false;
    }
    if (this.routeService.checkRoute(['back-office', 'events', 'view', ':id', 'edit'], state.url) && !this.authService.hasSelfServiceEventsManagePrivilege) {
      canAccess = false;
    }

    if (redirectToAdmin || redirectToReports || redirectToBOEvents) {
      if (redirectToAdmin) {
        this.router.navigate([this.authService.adminRoute]);
      } else if (redirectToReports) {
        this.router.navigate([this.authService.reportsRoute]);
      } else if (redirectToBOEvents) {
        this.router.navigate([this.authService.backOfficeRoute]);
      }
    } else if (!canAccess) {
      this.router.navigate([this.authService.accessDeniedRoute]);
    }
    return canAccess;
  }
}
